import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoTip from './InfoTip';
import { TOKEN_COLOR } from './tokens';

// Colour of a stage box. `dashed` = a stage before the cycle starts.
const TONES = {
    grey: { fg: '#607d8b', border: '#e0e0e0', bg: '#fafafb', dashed: true },
    amber: { fg: TOKEN_COLOR.warning, border: '#f7d9b8', bg: '#fffcf6' },
    red: { fg: TOKEN_COLOR.danger, border: '#f2c9c9', bg: '#fffafa' },
    blue: { fg: TOKEN_COLOR.primary, border: '#c2dcf5', bg: '#f8fbff' },
};

function StageBox({ stage }) {
    const t = TONES[stage.tone] || TONES.grey;
    const clickable = !!stage.onClick;
    return (
        <Box
            onClick={stage.onClick}
            sx={{
                flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px',
                p: '8px 12px', borderRadius: '10px', bgcolor: t.bg,
                border: `${stage.selected ? 2 : 1}px ${t.dashed ? 'dashed' : 'solid'} ${stage.selected ? TOKEN_COLOR.primary : t.border}`,
                cursor: clickable ? 'pointer' : 'default',
                '&:hover': clickable ? { boxShadow: '0 3px 10px rgba(0,0,0,0.08)' } : {},
            }}
        >
            <Typography sx={{ fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.7px', textTransform: 'uppercase', color: TOKEN_COLOR.textMuted }}>
                {stage.step}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: t.fg, '& svg': { fontSize: 15 } }}>
                {stage.icon}
                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: t.fg }}>{stage.title}</Typography>
                <InfoTip title={stage.info} />
                <Typography sx={{ ml: 'auto', fontSize: '22px', fontWeight: 800, lineHeight: 1, color: t.fg }}>
                    {stage.loading ? <Skeleton variant='text' width={40} /> : (stage.value ?? '—')}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: '10.5px', color: TOKEN_COLOR.textMuted, lineHeight: 1.4 }}>
                {stage.desc} · <Box component='span' sx={{ fontWeight: 600, color: '#555' }}>Waiting on: {stage.waitingOn}</Box>
            </Typography>
            {stage.link && (
                <Typography
                    component={Link}
                    to={stage.link.to}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ fontSize: '10.5px', fontWeight: 700, color: TOKEN_COLOR.primary, textDecoration: 'none', mt: 'auto', '&:visited, &:hover': { color: TOKEN_COLOR.primary } }}
                >
                    {stage.link.label} →
                </Typography>
            )}
        </Box>
    );
}

// Appointment cycle as a row of stage boxes joined by arrows, with a loop note and an optional footer.
export default function StageFlow({ stages = [], loopNote, footer }) {
    return (
        <Box sx={{ bgcolor: '#fff', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', mb: 2, overflow: 'hidden' }}>
            <Box sx={{ p: '12px 16px 0' }}>
                <Box sx={{ display: 'flex', alignItems: 'stretch', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 0 } }}>
                    {stages.map((s, i) => (
                        <React.Fragment key={s.key}>
                            {i > 0 && (
                                <Box sx={{ display: { xs: 'none', md: 'grid' }, placeItems: 'center', width: 26, color: '#9e9e9e' }}>
                                    <ChevronRightIcon />
                                </Box>
                            )}
                            <StageBox stage={s} />
                        </React.Fragment>
                    ))}
                </Box>
                {loopNote && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, mb: footer ? 0 : 1 }}>
                        <Typography sx={{ fontSize: '11px', color: TOKEN_COLOR.textSecondary }}>{loopNote}</Typography>
                        <Box sx={{ flex: 1, borderTop: '1.5px dashed #d0d0d0' }} />
                    </Box>
                )}
            </Box>
            {footer && (
                <Box sx={{ mt: 1, px: '16px', py: '6px', borderTop: '1px dashed #e0e0e0', bgcolor: '#fcfcfd' }}>
                    {footer}
                </Box>
            )}
        </Box>
    );
}
